import {
  ASSIGN_TO_FILE,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
  WorkspaceType,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssignmentModal, AssignmentTab } from '../../shared/components/assignment/assignmentModal';
import { EditFileAddonsList } from './EditFileAddonsList';
import { EditFileProductsList } from './EditFileProductsList';

const GET_MEDIA_ASSIGNMENTS = `
  query GetMediaAssignments($mediaId: String!) {
    media {
      findMediaAssignments(mediaId: $mediaId) {
        products
        addons
      }
    }
  }
`;

type Props = {
  fileId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'products' | 'addons';
};

export const FileAssignmentForm = ({
  fileId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  const { data, isPending } = useCustomQuery({
    query: GET_MEDIA_ASSIGNMENTS,
    cacheKey: ['media', 'assignments', fileId],
    variables: { mediaId: fileId },
  });
  const found = data?.media?.findMediaAssignments;

  const { mutateAsync: assignToFile } = useCustomMutation({
    query: ASSIGN_TO_FILE,
  });

  const save = (field: 'productIds' | 'addonIds') =>
    async ({ selected }: { selected: string[] }) => {
      if (!currentWorkspace?.id) return;
      await assignToFile({
        input: {
          mediaId: fileId,
          workspaceId: currentWorkspace.id,
          [field]: selected,
        },
      } as never);
      queryClient.invalidateQueries({ queryKey: ['media'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['product'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['addon'], exact: false });
    };

  const tabs: AssignmentTab[] = [
    {
      key: 'products',
      label: t('file.tabs.products'),
      searchPlaceholder: t('file.assignments.productsSearchPlaceholder'),
      assignments: found?.products,
      onSave: save('productIds'),
      renderList: (p) => (
        <EditFileProductsList
          workspaceId={currentWorkspace!.id}
          fileId={fileId}
          maxHeight={p.maxHeight}
          keyword={p.keyword}
          assignments={p.assignments}
          updateAssignments={p.onAssignmentsChange}
        />
      ),
    },
  ];

  if (isOEM) {
    tabs.push({
      key: 'addons',
      label: t('file.tabs.addons'),
      searchPlaceholder: t('file.assignments.addonsSearchPlaceholder'),
      assignments: found?.addons,
      onSave: save('addonIds'),
      renderList: (p) => (
        <EditFileAddonsList
          workspaceId={currentWorkspace!.id}
          fileId={fileId}
          maxHeight={p.maxHeight}
          keyword={p.keyword}
          assignments={p.assignments}
          updateAssignments={p.onAssignmentsChange}
        />
      ),
    });
  }

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending || !currentWorkspace?.id}
      initialTabKey={initialTab}
      onComplete={onComplete}
      tabs={tabs}
    />
  );
};
